import React, { useEffect, useMemo, useState } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import airGarageWhiteLogo from '../../../assets/whiteLogo.png';
import Loader from 'components/Loader/Loader';
import { Icon } from 'semantic-ui-react';
import { ValidatedDiscountCode } from 'types';
import { discountCodeClient } from 'clients';
import AGMessage from 'components/AGMessage/AGMessage';
import styles from './DiscountCodeFlyer.module.css';
import { PromoCodeDiscountType } from 'constants/promoCodes';
import { fitImageToPage } from 'utils/pdf';

const PAGE_WIDTH_IN = 8.5;
const PAGE_HEIGHT_IN = 11;

type DiscountCodeFlyerProps = {
    spotName: string;
    discountCode: ValidatedDiscountCode;
};

export default function DiscountCodeFlyer({ spotName, ...props }: DiscountCodeFlyerProps) {
    const [downloading, setDownloading] = useState(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [discountCode, setDiscountCode] = useState(props.discountCode);

    const { discountMessage, description } = useMemo(() => {
        const { formatted_value: formattedValue, discount_type: discountType, code } = discountCode || {};
        const discountTypeWording = discountType === PromoCodeDiscountType.MINUTES_TO_DISCOUNT ? 'free' : 'off';

        return {
            discountMessage:
                discountType === PromoCodeDiscountType.RATE_OVERRIDE
                    ? formattedValue
                    : `${formattedValue} ${discountTypeWording} parking code`,
            description: `Scan below or use code "${code}"`,
        };
    }, [discountCode]);

    useEffect(() => {
        async function getQrCode() {
            if (!discountCode.qr_code) {
                setLoading(true);
                try {
                    const discountCodeWithQR = await discountCodeClient.generateDiscountQrCode(discountCode.pk);
                    setDiscountCode(discountCodeWithQR);
                } catch (err) {
                    setErrorMessage('Something went wrong! Please try again later');
                } finally {
                    setLoading(false);
                }
            }
        }
        getQrCode();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function downloadPDF() {
        setDownloading(true);
        const qrCodeDownloadButton = document.querySelector('#qrCodeDownloadButton') as Element;
        const capture = document.querySelector('#qrCodeFlyer') as HTMLElement;
        html2canvas(capture, {
            scale: 4,
            useCORS: true,
            backgroundColor: '#fff',
            ignoreElements: (element) => {
                return [qrCodeDownloadButton].includes(element);
            },
        }).then((canvas) => {
            const imgData = canvas.toDataURL('image/png');
            const doc = new jsPDF('p', 'in', [PAGE_WIDTH_IN, PAGE_HEIGHT_IN]);
            const { x, y, width, height } = fitImageToPage(
                canvas.width,
                canvas.height,
                doc.internal.pageSize.getWidth(),
                doc.internal.pageSize.getHeight()
            );
            doc.addImage(imgData, 'PNG', x, y, width, height, undefined, 'FAST');
            doc.save(`${`${spotName}_${discountMessage}`.replaceAll(' ', '_')}.pdf`);
            setDownloading(false);
        });
    }

    return (
        <>
            {errorMessage && <AGMessage color="error">{errorMessage}</AGMessage>}

            <div className={styles.flyerWrapper} id="qrCodeFlyer">
                <div className={styles.flyerHeader}>
                    <img className={styles.flyerAGLogo} src={airGarageWhiteLogo} alt="AirGarage" />
                    <h1 contentEditable={true} className={styles.flyerTitle}>
                        {spotName}
                        <br />
                        {discountMessage}
                    </h1>
                    {!loading && (
                        <button
                            id="qrCodeDownloadButton"
                            onClick={downloadPDF}
                            className={styles.flyerDownloadButton}
                            disabled={downloading}
                        >
                            <Icon name="download" size="small" className={styles.flyerDownloadIcon} />
                        </button>
                    )}
                </div>
                <div className={styles.flyerMain}>
                    <h2 contentEditable={true} className={styles.flyerSubtitle}>
                        {description}
                    </h2>
                    <div className={styles.flyerQrCode}>
                        {loading ? (
                            <Loader size="sm" />
                        ) : (
                            <img
                                src={discountCode.qr_code}
                                alt={discountMessage}
                                className={styles.flyerQrCodeImg}
                                crossOrigin="anonymous"
                            />
                        )}
                    </div>
                    <h6 className={styles.flyerLabel}>For assistance or more information:</h6>
                    <span className={styles.flyerValue}>help.airgarage.com</span>
                </div>
            </div>
        </>
    );
}
